import {
  AlertTriangle,
  Bookmark,
  CheckCircle,
  Flag,
  Heart,
  Lightbulb,
  MessageCircle,
  Pencil,
  Pin,
  Star,
  StickyNote,
  Zap,
  type LucideIcon,
} from "lucide-react";
import {
  ANNOTATION_ICON_NAMES,
  DEFAULT_ANNOTATION_ICON,
  type AnnotationIconName,
} from "../types";

const ANNOTATION_ICON_MAP: Record<AnnotationIconName, LucideIcon> = {
  StickyNote,
  Bookmark,
  Star,
  AlertTriangle,
  CheckCircle,
  Flag,
  Heart,
  Lightbulb,
  MessageCircle,
  Pencil,
  Pin,
  Zap,
};

const DARK_ICON_COLOR = "#1a1a1a";
const LIGHT_ICON_COLOR = "#ffffff";
const LUMINANCE_THRESHOLD = 0.55;
const HEX_COLOR_RE = /^#?[0-9a-fA-F]{6}$/;

/**
 * Returns the LucideIcon component for a given icon name.
 * Falls back to the default icon if the name is not recognized.
 */
export function getAnnotationIcon(name: string): LucideIcon {
  if (isAnnotationIconName(name)) {
    return ANNOTATION_ICON_MAP[name];
  }
  return ANNOTATION_ICON_MAP[DEFAULT_ANNOTATION_ICON];
}

function isAnnotationIconName(name: string): name is AnnotationIconName {
  return (ANNOTATION_ICON_NAMES as readonly string[]).includes(name);
}

/**
 * Determines whether to use a dark or light icon color based on the
 * perceived luminance of the background color.
 * Returns a hex color string suitable for the icon stroke.
 */
export function getIconContrastColor(backgroundColor: string): string {
  if (!HEX_COLOR_RE.test(backgroundColor)) {
    return DARK_ICON_COLOR;
  }

  const hex = backgroundColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return DARK_ICON_COLOR;
  }

  // Relative luminance formula (ITU-R BT.709)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > LUMINANCE_THRESHOLD ? DARK_ICON_COLOR : LIGHT_ICON_COLOR;
}
